import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Header = ({ activeSection }) => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 1. Estado inicial del tema
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') || 'dark'
  );

  // 2. Efecto para alternar la clase 'dark' en <html> y <body>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    localStorage.setItem('user_lang', newLang);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'home', label: t('header.home') },
    { id: 'experience', label: t('header.experience') },
    { id: 'projects', label: t('header.projects') },
    { id: 'education', label: t('header.education') },
    { id: 'contact', label: t('header.contact') }
  ];

  // 3. Clases fijas según el tema activo (sin depender de scroll)
  const isDark = theme === 'dark';

  const headerBgClass = isDark
    ? 'bg-slate-900/90 backdrop-blur-md shadow-sm border-b border-slate-800'
    : 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200';

  const logoClass = isDark
    ? 'text-slate-100 hover:text-indigo-400'
    : 'text-slate-900 hover:text-blue-600';

  const navButtonActiveClass = isDark
    ? 'text-indigo-400 bg-indigo-500/10'
    : 'text-blue-600 bg-blue-50';

  const navButtonInactiveClass = isDark
    ? 'text-slate-300 hover:text-white hover:bg-slate-800'
    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${headerBgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <button 
            onClick={() => scrollToSection('home')} 
            className={`text-lg md:text-xl font-bold transition-colors ${logoClass}`} 
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Luis Castro
          </button>

          {/* Navegación Desktop */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeSection === item.id ? navButtonActiveClass : navButtonInactiveClass
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Botón de Idioma */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage} 
              className={`ml-2 flex gap-1.5 ${isDark ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-blue-600 hover:bg-blue-50'}`}
            >
              <Globe size={16} />
              {(i18n.language || 'es').toUpperCase()}
            </Button>

            {/* Botón Modo Claro / Oscuro */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme} 
              className={`ml-1 ${isDark ? 'text-amber-400 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`} 
              title="Cambiar tema"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </nav>

          {/* Controles Mobile */}
          <div className="flex items-center md:hidden gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleTheme} 
              className={`p-2 ${isDark ? 'text-amber-400' : 'text-slate-700'}`}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>

            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage} 
              className={`px-2 ${isDark ? 'text-indigo-400' : 'text-blue-600'}`}
            >
              {(i18n.language || 'es').toUpperCase()}
            </Button>

            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className={isDark ? 'text-slate-200' : 'text-slate-800'}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Desplegable Mobile */}
        {isMobileMenuOpen && (
          <nav className={`md:hidden py-4 border-t backdrop-blur-md ${
            isDark ? 'border-slate-800 bg-slate-900/95' : 'border-slate-200 bg-white/95'
          }`}>
            {navItems.map((item) => (
              <button 
                key={item.id} 
                onClick={() => scrollToSection(item.id)} 
                className={`block w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                  activeSection === item.id ? navButtonActiveClass : navButtonInactiveClass
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;